import axios from 'axios';
import Cookies from "js-cookie";
import { applyEnvironmentToRequest } from '../utils/environment';

const userId = JSON.parse(localStorage.getItem('userInfo'))?._id;
const api = axios.create({
    baseURL: process.env.REACT_APP_API_BASE_URL,
    headers: {
        'Content-Type': 'application/json',
        'User-Id': userId,
    },
});

const formApi = axios.create({
    baseURL: process.env.REACT_APP_API_BASE_URL,
    headers: {
        'Content-Type': 'multipart/form-data',
    },
});

const handleUnauthorized = (error) => {
    if (error.response && error.response.status === 401) {
        localStorage.removeItem('token');
        // Must pass the same path the cookie was set with ('/'), otherwise
        // js-cookie leaves it in place on deep routes and it diverges from the
        // cleared localStorage token, bouncing the user in a login loop.
        Cookies.remove('token', { path: '/' });
        window.location.href = '/login';
    }
    return Promise.reject(error);
};

// Read the `exp` (seconds since epoch) out of a JWT without verifying it.
// Returns 0 for null/malformed tokens so they always compare as "older".
const getTokenExp = (token) => {
    try {
        const payload = token.split('.')[1];
        const json = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
        return JSON.parse(json).exp || 0;
    } catch {
        return 0;
    }
};

// Sliding session: the backend re-issues a fresh JWT in `x-refreshed-token`
// once the current one is past half its lifetime — swap it in so active
// users are never logged out.
const handleTokenRefresh = (response) => {
    const refreshedToken = response.headers['x-refreshed-token'];
    if (refreshedToken) {
        // Only adopt the refreshed token if it actually outlives the one we
        // already hold. A cached HTTP response can replay a now-stale
        // `x-refreshed-token` from an earlier session; blindly storing it
        // clobbers a fresh login token with an expired one and bounces the
        // user into a /login loop (works in another browser because its cache
        // is empty). Comparing `exp` makes a stale replay a no-op.
        const current = localStorage.getItem('token');
        if (getTokenExp(refreshedToken) > getTokenExp(current)) {
            localStorage.setItem('token', refreshedToken);
            Cookies.set('token', refreshedToken, { path: '/' });
        }
    }
    return response;
};

// Attach the interceptor to both `api` and `formApi`
api.interceptors.response.use(handleTokenRefresh, handleUnauthorized);
formApi.interceptors.response.use(handleTokenRefresh, handleUnauthorized);

// Add a request interceptor
api.interceptors.request.use(
    function (config) {
        const token = localStorage.getItem('token');
        if (token) {
            config.headers.Authorization = `Bearer ${token}`;
        }
        // Stamp the environment the user is working in. Production attaches
        // nothing, so requests are byte-identical to what they were before
        // environments existed.
        return applyEnvironmentToRequest(config);
    },
    function (error) {
        return Promise.reject(error);
    }
);

formApi.interceptors.request.use(
    function (config) {
        const token = localStorage.getItem('token');
        if (token) {
            config.headers.Authorization = `Bearer ${token}`;
        }
        return applyEnvironmentToRequest(config);
    },
    function (error) {
        return Promise.reject(error);
    }
);

export const createUser = (user) => api.post('/auth/signup', user);
export const signInUser = (user) => api.post('/auth/login', user);
export const fetchFilteredUsers = (filter) => api.get(`/dashboard/user/filter?email=${filter}`);
export const forgotPassword = (email) => api.post(`/auth/forgot-password`, email);
export const verifyCode = (code) => api.post(`/auth/verify-code`, code);
export const resetPassword = (password) => api.post(`/auth/reset-password`, password);

export const createContentType = (content) => api.post('/dashboard/content-type', content);
export const fetchContentTypes = (id) => api.get(`/dashboard/content-type/${id}`);
// AI content-model finder: { sampleData?, image? (data URL) } -> { match, proposal, reasoning }
export const aiMatchContentModel = (workspaceId, payload) => api.post(`/dashboard/content-type/${workspaceId}/ai-match`, payload);
export const deleteContentType = (id) => api.delete(`/dashboard/content-type/${id}`);
export const editContentType = (id, content) => api.post(`dashboard/content-type/${id}`, content);

export const createWorkspace = (data) => api.post('/dashboard/workspace', data);
export const fetchWorkspaces = () => api.get('/dashboard/workspace');
export const loginWorkspace = (id) => api.get(`/dashboard/workspace/${id}`);
export const fetchWorkspaceByRole = (role) => api.get(`/dashboard/workspace/user/${role}`);
export const deleteWorkspace = (id) => api.delete(`/dashboard/workspace/${id}`);
export const updateWorkspace = (workspaceId, data) => api.put(`/dashboard/workspace/update/${workspaceId}`, data);
export const flushCdnCache = (workspaceId, paths) => api.post(`/dashboard/workspace/${workspaceId}/cdn/invalidate`, { paths });

export const getContentType = (workspaceId, id) => api.get(`/dashboard/content-type/${workspaceId}/${id}`);
export const getEntries = (workspaceId, params = {}) => api.get(`/dashboard/entries/${workspaceId}`, { params });
export const createEntry = (entry) => api.post('/dashboard/entry', entry);
// AI generate entry: { contentTypeId, exampleEntryIds?, brief, locale?, image? } -> { fields, referenceFields, reasoning }
export const aiGenerateEntry = (workspaceId, payload) => api.post(`/dashboard/entry/ai-generate/${workspaceId}`, payload);
export const getEntry = (workspaceId, id) => api.get(`/dashboard/entry/${workspaceId}/${id}`);
export const getEntryDetails = (workspaceId, ids) => {
    const idString = Array.isArray(ids) ? ids.join(',') : ids;
    return api.get(`/dashboard/entryDetails/workspace/${workspaceId}?ids=[${idString}]`);
}
export const updateEntry = (id, data) => api.put(`/dashboard/entry/${id}`, data);
export const scheduleEntry = (workspaceId, id, data) => api.put(`/dashboard/entry/schedule/${workspaceId}/${id}`, data);
export const updatePreviewEntry = (id, data) => api.put(`/dashboard/entry-preview/${id}`, data)
export const deleteEntry = (id) => api.delete(`/dashboard/entry/${id}`);
export const bulkDeleteEntries = (ids) => api.post('/dashboard/entry/bulk-delete', { ids });
export const getEntryVersionsList = (workspaceId, entryId) => api.get(`/dashboard/entry-versions/${workspaceId}/${entryId}`);

export const fetchAssets = (id, page = 1, searchTerm = '') => api.get(`/asset/uploads/${id}/${page}/${searchTerm}`);
export const addAsset = (data) => formApi.post('/asset/upload', data);
export const deleteAsset = (id) => api.delete(`/asset/${id}`);
export const editAsset = (id, data) => api.put(`/asset/update/${id}`, data);
// Entries (in every environment) whose media fields reference this asset.
export const fetchAssetUsage = (id, page = 1, limit = 50) => api.get(`/asset/${id}/usage`, { params: { page, limit } });

// Management console (platform admins only — see backend/src/admin).
export const fetchAdminMe = () => api.get('/admin/me');
export const fetchAdminSummary = () => api.get('/admin/summary');
export const fetchAdminWorkspaces = (params) => api.get('/admin/workspaces', { params });
export const fetchAdminWorkspace = (id) => api.get(`/admin/workspaces/${id}`);
export const fetchAdminUsers = (params) => api.get('/admin/users', { params });

export const fetchApps = () => api.get(`/app-studio/apps`);
export const createApp = (data) => api.post('/app-studio', data);
export const deleteApp = (id) => api.delete(`/app-studio/app/${id}`);
export const editApp = (id, data) => api.post(`/app-studio/${id}`, data);
export const installApp = (id, workspaceId) => api.post(`/app-studio/workspace/${workspaceId}/install/${id}`);


export const fetchWebHooks = (workspaceId) => api.get(`/dashboard/webhook/${workspaceId}`);
export const getWebHook = (workspaceId, id) => api.get(`/dashboard/webhook/${workspaceId}/${id}`);
export const createWebhooks = (data) => api.post('/dashboard/webhook', data);
export const deleteWebHook = (id) => api.delete(`/dashboard/webhook/${id}`);
export const editWebHook = (id, data) => api.post(`/dashboard/webhook/${id}`, data);
export const createPrompt = (data) => api.post('/prompts', data);
export const fecthPrompts = (id) => api.get(`/prompts/${id}`);
export const getPromptById = (workspaceId, id) => api.get(`/prompts/${workspaceId}/${id}`);
export const deletePrompt = (id) => api.delete(`/prompts/${id}`);
export const editPrompt = (data, id) => api.post(`/prompts/${id}`, data);

export const fetchActivityLogs = (workspaceId, page = 1, searchTerm = '') => api.get(`/activity-log/workspace/${workspaceId}/${page}`);
export const fetchDashboardStats = (workspaceId) => api.get(`/dashboard/stats/${workspaceId}`);
export const fetchOAuthClients = () => api.get(`/auth/oauth/clients`);
export const createOAuthClient = (data) => api.post('/auth/oauth/client', data);
export const updateOAuthClient = (id, data) => api.put(`/auth/oauth/client/${id}`, data);
export const deleteOAuthClient = (id) => api.delete(`/auth/oauth/client/${id}`);
export const translateEntry = (workspaceId, id, locale) => api.post(`translate-entry/workspace/${workspaceId}/content/${id}/${locale}`);
export const duplicateEntry = (workspaceId, id) => api.post(`/dashboard/entry/duplicate/${workspaceId}/${id}`);

// Entry comments
export const fetchEntryComments = (workspaceId, entryId, params = {}) => api.get(`/comment/entry/${workspaceId}/${entryId}`, { params });
export const fetchEntryCommentCounts = (workspaceId, entryId) => api.get(`/comment/entry/${workspaceId}/${entryId}/counts`);
export const createEntryComment = (workspaceId, entryId, data) => api.post(`/comment/${workspaceId}/${entryId}`, data);
export const updateEntryComment = (commentId, data) => api.put(`/comment/${commentId}`, data);
export const resolveEntryComment = (commentId, resolved) => api.patch(`/comment/${commentId}/resolve`, { resolved });
export const deleteEntryComment = (commentId) => api.delete(`/comment/${commentId}`);

// Environments — a workspace's isolated copies of its content models and
// entries. `Production` is the default and is what the public read API serves
// when no environment is named.
export const fetchEnvironments = (workspaceId, withCounts = false) =>
    api.get(`/dashboard/environment/${workspaceId}`, { params: withCounts ? { withCounts: true } : {} });
export const createEnvironment = (workspaceId, data) => api.post(`/dashboard/environment/${workspaceId}`, data);
export const updateEnvironment = (workspaceId, id, data) => api.put(`/dashboard/environment/${workspaceId}/${id}`, data);
export const deleteEnvironment = (workspaceId, id) => api.delete(`/dashboard/environment/${workspaceId}/${id}`);
// Copy or move content models and entries between two environments.
//
// `dryRun: true` answers inline with the plan. Applying it answers 202 with a
// `jobId`: the work is one write per entry, per locale and per reference, so it
// runs on a queue rather than on the request. Poll it with
// `fetchEnvironmentJob` — creating a seeded environment and deleting one hand
// back a `jobId` the same way.
export const transferEnvironmentContent = (workspaceId, data) =>
    api.post(`/dashboard/environment/${workspaceId}/transfer`, data);
export const fetchEnvironmentJob = (workspaceId, jobId) =>
    api.get(`/dashboard/environment/${workspaceId}/transfer/${jobId}`);

// Per-environment access. A member's workspace role applies everywhere unless
// an environment names a different one — so `environmentRoles` is an override
// map, and clearing an entry (role: null) returns that environment to the
// workspace role rather than removing access.
export const fetchEnvironmentAccess = (workspaceId) =>
    api.get(`/dashboard/environment/${workspaceId}/access/list`);
// `environment` is an id or slug; omit it for the default (production) one.
export const setEnvironmentAccess = (workspaceId, { userId, environment, role }) =>
    api.put(`/dashboard/environment/${workspaceId}/access`, { userId, environment, role });

// Read another environment's content without leaving the one the user is in —
// the transfer screen shows the source environment while the dashboard stays
// pointed at the active one. An explicit `environment` param overrides the
// ambient selection (see utils/environment.js).
export const fetchContentTypesInEnvironment = (workspaceId, environment) =>
    api.get(`/dashboard/content-type/${workspaceId}`, { params: { environment: environment || 'production' } });
export const fetchEntriesInEnvironment = (workspaceId, environment, params = {}) =>
    api.get(`/dashboard/entries/${workspaceId}`, {
        params: { environment: environment || 'production', ...params },
    });
